import { SETTINGS_STORAGE_KEYS } from "@/lib/localStorageKeys/settings";
import { DEFAULT_SETTINGS, LANGUAGES } from "./default-data";
import { Settings } from "./types";

export const getStoredSettings = (): Settings => {
  if (typeof window === "undefined") {
    return DEFAULT_SETTINGS;
  }

  try {
    const storedData = localStorage.getItem(SETTINGS_STORAGE_KEYS.CORE);

    if (!storedData) {
      // If no settings exist, save and return defaults
      saveSettings(DEFAULT_SETTINGS);
      return DEFAULT_SETTINGS;
    }

    const parsedData = JSON.parse(storedData);

    // Merge with defaults to ensure all fields exist
    const merged = {
      ...DEFAULT_SETTINGS,
      ...parsedData,
    };

    // Coerce legacy/disabled language ids ("system", "de", "zh-cn", …) back to "en".
    const language = LANGUAGES.find((lang) => lang.id === merged.language);
    if (!language || !language.enabled) {
      merged.language = "en";
    }

    return merged;
  } catch (error) {
    console.error("Error loading settings from localStorage:", error);
    return DEFAULT_SETTINGS;
  }
};

export const saveSettings = (settings: Settings): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(SETTINGS_STORAGE_KEYS.CORE, JSON.stringify(settings));
  } catch (error) {
    console.error("Error saving settings to localStorage:", error);
  }
};

/**
 * Get the size of stored settings in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredSettingsValue(): number {
  if (typeof window === "undefined") return 0;

  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEYS.CORE);

    if (!stored) return 0;

    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error("Error getting journal size:", error);
    return 0;
  }
}

/**
 * Clear stored settings
 * @returns Success status
 */
export function clearStoredSettings(): boolean {
  if (typeof window === "undefined") return false;

  try {
    localStorage.removeItem(SETTINGS_STORAGE_KEYS.CORE);
    return true;
  } catch (error) {
    console.error("Error clearing journal:", error);
    return false;
  }
}
